// AlertComponent.js
import { LitElement, html, css } from 'lit';
import { clearAlert } from './AlertSlice';
import alertStore from './AlertStore';

class AlertComponent extends LitElement {
  static styles = css`
    :host {
      display: none;
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      /* 最前面に表示 */
      z-index: 99999;
    }

    /* 背景：画面全体を覆い、中身（.alert）を中央に配置 */
    .background {
      width: 100%;
      height: 100%;
      background-color: rgba(0, 0, 0, 0.5);
      display: flex;
      justify-content: center;
      align-items: center;
    }

    /* アラートダイアログ本体 */
    .alert {
      position: relative;
      display: flex;
      flex-direction: column;
      background-color: white;
      padding: 24px;
      border-radius: 12px; /* 少し角丸を強くしてモダンに */
      box-shadow: 0 10px 25px rgba(0, 0, 0, 0.3);
      
      /* --- 横幅の調整（ここが重要） --- */
      width: auto;            /* コンテンツの長さに合わせる */
      min-width: 280px;      /* 短すぎると不自然なので最小幅を設定 */
      max-width: 90%;        /* 画面端に密着しないよう最大幅を制限 */
      
      /* 縦幅の調整 */
      max-height: 80vh;      /* 非常に長いメッセージでもボタンが隠れないように制限 */
      box-sizing: border-box;
      
      /* アニメーション：ふわっと表示 */
      animation: alert-pop 0.2s ease-out;
    }

    @keyframes alert-pop {
      from { opacity: 0; transform: scale(0.95); }
      to { opacity: 1; transform: scale(1); }
    }

    /* メッセージ表示エリア */
    .message-container {
      flex-grow: 1;
      overflow-y: auto;        /* 長文時はスクロール */
      margin-bottom: 20px;
      padding: 0 4px;         /* スクロールバーとの間に少し隙間 */
      word-break: break-all;
      overflow-wrap: break-word;
    }

    .alert p {
      margin: 0 0 12px 0;
      font-size: 16px;
      line-height: 1.5;
      color: #333;
      /* 短い文の時は中央に、長い文の時は左寄りに見えるよう調整 */
      text-align: center;
      white-space: pre-wrap; 
    }

    .alert p:last-of-type {
      margin-bottom: 0;
    }

    /* 閉じるボタン */
    .alert button {
      flex-shrink: 0;
      padding: 12px 24px;
      border: none;
      border-radius: 8px;
      background-color: rgb(0, 123, 255);
      color: white;
      font-size: 16px;
      font-weight: bold;
      cursor: pointer;
      transition: background-color 0.2s, filter 0.3s;
      align-self: center;      /* ボタンを中央に配置 */
      width: 100%;             /* ダイアログの幅いっぱいに広げる */
      max-width: 160px;        /* 広がりすぎないように制限 */
    }
      
    .alert button:hover {
      filter: brightness(90%);
      background-color: rgb(0, 100, 210);
    }

    .alert button:active {
      transform: scale(0.98);
    }

    .alert button:focus {
      outline: 3px solid rgba(0, 123, 255, 0.4);
      outline-offset: 2px;
    }
  `;

  static get properties() {
    return {
      showAlert: { type: Boolean },
      alertMessage: { type: String },
    };
  }

  constructor() {
    super();
    this.showAlert = false;
    this.alertMessage = '';

    // Redux store の変更を購読
    alertStore.subscribe(() => {
      const state = alertStore.getState();
      this.showAlert = state.alert.showAlert;
      this.alertMessage = state.alert.alertMessage;

      if (this.showAlert) {
        this.style.display = 'flex';
        requestAnimationFrame(() => {
          //閉じるボタンにフォーカス
          const button = this.shadowRoot.getElementById('closeButton');
          button.focus();
        })

      } else {
        this.style.display = 'none';
      }
    });
  }

  handleAlertClose() {
    alertStore.dispatch(clearAlert());
  }

  render() {
    const formattedMessage = this.alertMessage.replace(/\\n/g, '\n');
    return html`
      ${this.showAlert ? html`
        <div class="background">
          <div class="alert">
            <!-- メッセージ部分だけをスクロール対象にする -->
            <div class="message-container">
              <p>${formattedMessage}</p>
            </div>
            <button id="closeButton" @click="${this.handleAlertClose}">閉じる</button>
          </div>
        </div>
      ` : ''}
    `;
  }
}

customElements.define('alert-component', AlertComponent);